import React, { useState, useEffect } from 'react';
import { roleLabel } from '../../utils/roleLabels';
import { ApiClient, type ComplianceOverviewItem } from '../../api/client';
import type { PageItem } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { useLayout } from '../../context/LayoutContext';
import { downloadComplianceXLSX, downloadCompliancePDF } from '../../utils/export';
import { 
  Building, 
  Building2,
  Plus, 
  RefreshCw,
  FileSpreadsheet,
  FileText,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  UserCheck,
  Search,
  ArrowRight,
  Upload,
  Layers,
  Clock,
  Info
} from 'lucide-react';

interface PageStats {
  page: PageItem;
  /** elvárt tételek: bejegyzések + bejegyzés nélküli sablonpontok */
  totalRecords: number;
  completedRecords: number;
  percentage: number;
  missing: number;
}

type TenantOverviewItem = ComplianceOverviewItem;

const CATEGORY_LABEL: Record<string, string> = {
  dpo_attachment: 'kötelező melléklet hiányzik (DPO)',
  dpo_data: 'kötelező adat hiányzik (DPO)',
  fields: 'kötelező mező üres',
  placeholder: 'csak import helyőrző („Mappa: …”)',
  empty: 'nincs tartalom és melléklet',
  uncovered: 'a 2026-os lista pontjához nincs bejegyzés',
};

export const ComplianceDashboardView: React.FC<{ onOpenWizard: () => void }> = () => {
  const { tenant, role, impersonateTenant } = useAuth();
  const { setActiveCategory, setActiveModule, setActivePageId } = useLayout();

  const isSuperadmin = role === 'superadmin' || role === 'tenant_admin';

  // Active Tab: 'multi' for multi-tenant compliance overview, 'single' for local tenant drilldown
  const [activeTab, setActiveTab] = useState<'multi' | 'single'>(() => {
    return isSuperadmin ? 'multi' : 'single';
  });

  // Single Tenant Data
  const [pages, setPages] = useState<PageItem[]>([]);
  const [pageStats, setPageStats] = useState<PageStats[]>([]);
  const [loadingSingle, setLoadingSingle] = useState(true);

  // Multi-Tenant Overview Data
  const [overviewData, setOverviewData] = useState<TenantOverviewItem[]>([]);
  const [loadingMulti, setLoadingMulti] = useState(false);
  const [multiSearch, setMultiSearch] = useState('');
  const [onlyWithMissing, setOnlyWithMissing] = useState(false);
  const [expandedTenants, setExpandedTenants] = useState<Set<number>>(new Set());
  const [exporting, setExporting] = useState(false);

  // New Page Modal
  const [isNewPageModalOpen, setIsNewPageModalOpen] = useState(false);
  const [newPageTitle, setNewPageTitle] = useState('');
  const [newPageDesc, setNewPageDesc] = useState('');
  const [newPageIcon, setNewPageIcon] = useState('📋');
  const [isCreatingPage, setIsCreatingPage] = useState(false);

  // Saját szervezet: a szerver szigorú kiértékelése (ugyanaz, mint a több szervezetes nézetben és az XLSX-ben)
  const [single, setSingle] = useState<ComplianceOverviewItem | null>(null);
  const [showAllMissing, setShowAllMissing] = useState(false);
  const fetchSingleData = async () => {
    setLoadingSingle(true);
    const [res, ov] = await Promise.all([ApiClient.getPages(), ApiClient.getComplianceOverview(tenant?.id ? Number(tenant.id) : undefined)]);
    const item = ov.ok && ov.data ? (ov.data.find((x) => x.tenant_id === Number(tenant?.id)) || ov.data[0] || null) : null;
    setSingle(item);
    if (res.ok && res.data) {
      setPages(res.data);
      const byId = new Map((item?.pages || []).map((p) => [p.page_id, p]));
      setPageStats(res.data.map((p) => {
        const st = byId.get(p.id);
        return { page: p, totalRecords: st?.expected ?? 0, completedRecords: st?.completed ?? 0, percentage: st?.percentage ?? 0, missing: (st?.missing ?? 0) + (st?.uncovered ?? 0) };
      }));
    }
    setLoadingSingle(false);
  };

  // Load multi-tenant overview
  const fetchMultiData = async () => {
    setLoadingMulti(true);
    const res = await ApiClient.getComplianceOverview();
    if (res.ok && res.data) {
      setOverviewData(res.data);
      // Automatically expand tenants that have missing items
      const withMissing = new Set<number>();
      for (const item of res.data) {
        if (item.missing_required_count > 0) {
          withMissing.add(item.tenant_id);
        }
      }
      setExpandedTenants(withMissing);
    }
    setLoadingMulti(false);
  };

  useEffect(() => {
    fetchSingleData();
    if (isSuperadmin) {
      fetchMultiData();
    }
  }, [isSuperadmin]);

  const totalPages = pages.length;
  const totalRecords = single?.total_expected ?? 0;
  const totalCompletedRecords = single?.completed_records ?? 0;
  const overallPercentage = single?.percentage ?? 0;
  const singleMissing = single ? single.missing_items.length + single.uncovered_points.length : 0;

  const handleOpenPage = (pageId: number) => {
    setActiveCategory('transparency_data');
    setActiveModule('records_manager');
    setActivePageId(pageId);
  };

  const handleCreatePage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPageTitle.trim()) return;
    setIsCreatingPage(true);
    const slug = newPageTitle
      .toLowerCase()
      .replace(/[áéíóöőúüű]/g, (m) => ({ á: 'a', é: 'e', í: 'i', ó: 'o', ö: 'o', ő: 'o', ú: 'u', ü: 'u', ű: 'u' }[m] || m))
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    const res = await ApiClient.createPage({
      title: newPageTitle,
      slug: slug || `oldal-${Date.now()}`,
      description: newPageDesc,
      icon: newPageIcon || '📋',
    });

    if (res.ok) {
      setIsNewPageModalOpen(false);
      setNewPageTitle('');
      setNewPageDesc('');
      fetchSingleData();
    } else {
      alert(res.error || 'Hiba az oldal létrehozásakor');
    }
    setIsCreatingPage(false);
  };

  const toggleTenantAccordion = (id: number) => {
    setExpandedTenants((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Impersonate and navigate directly to fix the missing point
  const handleFixMissingItem = async (tenantId: number, tenantName: string, pageId?: number) => {
    if (!window.confirm(`Átlépés a(z) "${tenantName}" felületére a kötelező feltöltés pótlásához?`)) {
      return;
    }
    const res = await impersonateTenant(tenantId);
    if (res.ok) {
      setActiveCategory('transparency_data');
      setActiveModule('records_manager');
      if (pageId) {
        setActivePageId(pageId);
      }
    } else {
      alert(res.error || 'Hiba a szervezet profiljába való átlépéskor');
    }
  };

  const handleExportXLSX = async () => {
    setExporting(true);
    try {
      await downloadComplianceXLSX();
    } catch (err: any) {
      alert('Hiba az XLSX exportálásakor: ' + (err.message || err));
    }
    setExporting(false);
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      await downloadCompliancePDF(filteredOverview);
    } catch (err: any) {
      alert('Hiba a PDF exportálásakor: ' + (err.message || err));
    }
    setExporting(false);
  };

  const getRoleDisplay = (r: string) => roleLabel(r);


  const getProgressBarColor = (pct: number) => {
    if (pct < 40) return 'bg-rose-500';
    if (pct < 80) return 'bg-amber-500';
    return 'bg-emerald-500';
  };

  // Filter multi-tenant list
  const filteredOverview = overviewData.filter((item) => {
    const matchesQuery = 
      item.tenant_name.toLowerCase().includes(multiSearch.toLowerCase()) ||
      item.tenant_slug.toLowerCase().includes(multiSearch.toLowerCase()) ||
      (item.short_name && item.short_name.toLowerCase().includes(multiSearch.toLowerCase()));
    if (!matchesQuery) return false;
    if (onlyWithMissing && item.missing_required_count === 0) return false;
    return true;
  });

  // Aggregated calculations for multi-tenant overview
  const totalTenantsCount = overviewData.length;
  const tenantsWithRecords = overviewData.filter(i => i.total_records > 0);
  const pendingTenantsCount = overviewData.filter(i => i.total_records === 0).length;
  const perfectTenantsCount = tenantsWithRecords.filter(i => i.percentage === 100).length;
  const totalMissingRequiredCount = overviewData.reduce((acc, i) => acc + i.missing_required_count, 0);
  const avgPercentage = tenantsWithRecords.length > 0 
    ? Math.round(tenantsWithRecords.reduce((acc, i) => acc + i.percentage, 0) / tenantsWithRecords.length) 
    : 0;

  return (
    <div className="space-y-8 max-w-6xl mx-auto pb-12">
      {/* ── TOP HEADER & TAB SWITCHER (For Superadmin) ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white flex items-center gap-2.5">
            <Building2 className="w-7 h-7 text-indigo-600 dark:text-indigo-400" />
            <span>Jogszabályi Megfelelőségi Riportok</span>
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Kötelező közzétételi pontok, feltöltési állapot és hiányzó mellékletek felügyelete
          </p>
        </div>

        {isSuperadmin && (
          <div className="flex items-center bg-slate-100 dark:bg-slate-800 p-1 rounded-xl border border-slate-200 dark:border-slate-700 shadow-xs">
            <button
              onClick={() => setActiveTab('multi')}
              className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition cursor-pointer ${
                activeTab === 'multi'
                  ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-xs'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              <Layers className="w-3.5 h-3.5" />
              <span>Összes Intézmény (Multi-Tenant)</span>
            </button>
            <button
              onClick={() => setActiveTab('single')}
              className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition cursor-pointer ${
                activeTab === 'single'
                  ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-xs'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              <Building className="w-3.5 h-3.5" />
              <span>Saját Intézmény Részletei</span>
            </button>
          </div>
        )}
      </div>

      {/* ════════════════════════════════════════════════════════════════
          A) MULTI-TENANT MEGFELELŐSÉGI RIORT (SUPERADMIN RENDSZERNÉZET)
          ════════════════════════════════════════════════════════════════ */}
      {isSuperadmin && activeTab === 'multi' && (
        <div className="space-y-6">
          {/* Multi-Tenant Top Metric Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 shadow-xs">
              <div className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                Intézmények Száma
              </div>
              <div className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white mt-1">
                {totalTenantsCount}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">Regisztrált önkormányzat & szerv</div>
            </div>

            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 shadow-xs">
              <div className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                Átlagos Megfelelőség
              </div>
              <div className="text-2xl sm:text-3xl font-extrabold text-indigo-600 dark:text-indigo-400 mt-1">
                {avgPercentage}%
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                {tenantsWithRecords.length > 0 
                  ? `${tenantsWithRecords.length} aktív közzétevő alapján` 
                  : 'Nincs elindított feltöltés'}
              </div>
            </div>

            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 shadow-xs">
              <div className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                100%-os Szervezetek
              </div>
              <div className="text-2xl sm:text-3xl font-extrabold text-emerald-600 dark:text-emerald-400 mt-1">
                {perfectTenantsCount} / {tenantsWithRecords.length || totalTenantsCount}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                {pendingTenantsCount > 0 
                  ? `${pendingTenantsCount} még feltöltésre vár` 
                  : 'Minden kötelező tétel kitöltve'}
              </div>
            </div>

            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 shadow-xs">
              <div className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                Hiányzó Kötelező Melléklet
              </div>
              <div className={`text-2xl sm:text-3xl font-extrabold mt-1 ${
                totalMissingRequiredCount > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'
              }`}>
                {totalMissingRequiredCount} db
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                {totalMissingRequiredCount > 0 ? 'Azonnali pótlás javasolt' : 'Nincs elmaradt feltöltés'}
              </div>
            </div>
          </div>

          {/* Action Toolbar */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 shadow-xs">
            <div className="flex items-center gap-3 flex-1 max-w-md">
              <div className="relative w-full">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={multiSearch}
                  onChange={(e) => setMultiSearch(e.target.value)}
                  placeholder="Keresés szervezet neve vagy azonosítója szerint..."
                  className="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-800 rounded-xl text-slate-900 dark:text-slate-100 text-xs placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            <div className="flex items-center flex-wrap gap-2">
              <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 dark:text-slate-300 cursor-pointer bg-slate-50 dark:bg-slate-950 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-800">
                <input
                  type="checkbox"
                  checked={onlyWithMissing}
                  onChange={(e) => setOnlyWithMissing(e.target.checked)}
                  className="rounded text-indigo-600 focus:ring-indigo-500 w-4 h-4"
                />
                <span>Csak a hiánnyal rendelkezők</span>
              </label>

              <button
                onClick={handleExportXLSX}
                disabled={exporting || loadingMulti}
                className="flex items-center gap-1.5 px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-xl text-xs font-semibold transition cursor-pointer shadow-xs disabled:opacity-50"
                title="Teljes rendszer-riport letöltése Excel (XLSX) fájlként"
              >
                <FileSpreadsheet className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                <span>XLSX Riport</span>
              </button>

              <button
                onClick={handleExportPDF}
                disabled={exporting || loadingMulti}
                className="flex items-center gap-1.5 px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-xl text-xs font-semibold transition cursor-pointer shadow-xs disabled:opacity-50"
                title="Megfelelőségi és hiánylista jelentés letöltése PDF-ben"
              >
                <FileText className="w-4 h-4 text-rose-600 dark:text-rose-400" />
                <span>PDF Riport</span>
              </button>

              <button
                onClick={fetchMultiData}
                className="p-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-xl transition cursor-pointer shadow-xs"
                title="Frissítés"
              >
                <RefreshCw className={`w-4 h-4 ${loadingMulti ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Multi-Tenant List / Accordion */}
          <div className="space-y-3">
            {loadingMulti ? (
              <div className="py-16 text-center text-slate-500 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto text-indigo-500 mb-2" />
                Intézmények megfelelőségi adatainak lekérdezése...
              </div>
            ) : filteredOverview.length === 0 ? (
              <div className="py-12 text-center text-slate-500 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl">
                Nincs a keresésnek vagy a szűrőnek megfelelő szervezet.
              </div>
            ) : (
              filteredOverview.map((item) => {
                const isExpanded = expandedTenants.has(item.tenant_id);
                const isUninitialized = item.total_records === 0;
                const hasMissing = item.missing_required_count > 0;

                return (
                  <div
                    key={item.tenant_id}
                    className={`bg-white dark:bg-slate-900 border rounded-2xl overflow-hidden shadow-xs transition ${
                      hasMissing 
                        ? 'border-amber-300/80 dark:border-amber-900/60' 
                        : 'border-slate-200 dark:border-slate-800'
                    }`}
                  >
                    {/* Header Row */}
                    <div
                      onClick={() => toggleTenantAccordion(item.tenant_id)}
                      className="p-4 sm:px-6 flex flex-col md:flex-row md:items-center justify-between gap-4 cursor-pointer hover:bg-slate-50/70 dark:hover:bg-slate-800/40 select-none"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <button className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                          {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                        </button>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-base font-bold text-slate-900 dark:text-white truncate">
                              {item.tenant_name}
                            </span>
                            <span className="text-xs font-mono px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 font-semibold border border-slate-200 dark:border-slate-700">
                              /{item.tenant_slug}
                            </span>
                            {item.short_name && (
                              <span className="text-xs text-slate-500 font-medium">({item.short_name})</span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {item.total_pages} nyilvántartási oldal · {item.total_expected} elvárt tétel{item.uncovered_points?.length ? ` (ebből ${item.uncovered_points.length} bejegyzés nélküli listapont)` : ''} · {item.completed_records} kitöltve
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 shrink-0" onClick={(e) => e.stopPropagation()}>
                        {/* Progress bar */}
                        <div className="w-36 sm:w-44 space-y-1">
                          <div className="flex items-center justify-between text-xs font-mono font-bold">
                            <span className="text-slate-600 dark:text-slate-300">{item.completed_records}/{item.total_expected}</span>
                            <span className={
                              isUninitialized 
                                ? 'text-slate-400 dark:text-slate-500' 
                                : item.percentage === 100 
                                  ? 'text-emerald-600 dark:text-emerald-400' 
                                  : 'text-slate-900 dark:text-white'
                            }>
                              {isUninitialized ? '– %' : `${item.percentage}%`}
                            </span>
                          </div>
                          <div className="w-full bg-slate-100 dark:bg-slate-800 h-2 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all duration-300 ${
                                isUninitialized ? 'bg-slate-300 dark:bg-slate-700' : getProgressBarColor(item.percentage)
                              }`}
                              style={{ width: `${isUninitialized ? 0 : Math.max(item.percentage, 2)}%` }}
                            />
                          </div>
                        </div>

                        {/* Státusz jelvény (Badge) */}
                        {isUninitialized ? (
                          <span 
                            className="px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 flex items-center gap-1.5 whitespace-nowrap"
                            title="Ennél a szervezetnél még nem kezdődött meg az adatok feltöltése, nincs viszonyítási alap"
                          >
                            <Clock className="w-3.5 h-3.5 text-slate-400" />
                            <span>Feltöltésre vár</span>
                          </span>
                        ) : hasMissing ? (
                          <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-50 dark:bg-rose-950/50 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800 flex items-center gap-1.5 whitespace-nowrap">
                            <AlertTriangle className="w-3.5 h-3.5 text-rose-600 dark:text-rose-400" />
                            <span>{item.missing_required_count} db hiány</span>
                          </span>
                        ) : item.percentage === 100 ? (
                          <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 flex items-center gap-1.5 whitespace-nowrap">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                            <span>Megfelelő</span>
                          </span>
                        ) : (
                          <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 flex items-center gap-1.5 whitespace-nowrap">
                            <Clock className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                            <span>Folyamatban</span>
                          </span>
                        )}

                        {/* Direct Impersonate Button */}
                        <button
                          onClick={() => handleFixMissingItem(item.tenant_id, item.tenant_name)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-xs font-semibold shadow-xs transition cursor-pointer whitespace-nowrap"
                          title="Átlépés a szervezet felületére megbízás alapján az adatok feltöltéséhez és rendezéséhez"
                        >
                          <UserCheck className="w-3.5 h-3.5" />
                          <span>Átlépés</span>
                        </button>
                      </div>
                    </div>

                    {/* Expandable Details Section */}
                    {isExpanded && (
                      <div className="p-5 border-t border-slate-100 dark:border-slate-800/80 bg-slate-50/50 dark:bg-slate-950/40 space-y-3">
                        <div className="text-xs font-bold text-slate-700 dark:text-slate-300 flex items-center gap-2">
                          {isUninitialized ? (
                            <Info className="w-4 h-4 text-slate-400" />
                          ) : hasMissing ? (
                            <AlertTriangle className="w-4 h-4 text-amber-500" />
                          ) : (
                            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                          )}
                          <span>
                            {isUninitialized
                              ? 'Ennél a szervezetnél még nem kezdődött meg az adatok feltöltése (0 rögzített közzétételi pont).'
                              : hasMissing 
                                ? `Hiányos vagy hiányzó tételek ennél az intézménynél (${item.missing_required_count} db):` 
                                : 'Minden előírt kötelező feltöltés rendben van ennél a szervezetnél.'}
                          </span>
                        </div>

                        {isUninitialized ? (
                          <div className="p-4 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 text-xs text-slate-600 dark:text-slate-400 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-xs">
                            <div className="space-y-1">
                              <p className="font-semibold text-slate-800 dark:text-slate-200">
                                Még nincs feltöltött közzétételi pont
                              </p>
                              <p className="text-slate-500">
                                A jogszabályi megfelelőség méréséhez töltsön be mappastruktúrát, mintasablont vagy rögzítsen közzétételi adatokat az intézmény felületén!
                              </p>
                            </div>
                            <button
                              onClick={() => handleFixMissingItem(item.tenant_id, item.tenant_name)}
                              className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs font-semibold whitespace-nowrap shrink-0 transition flex items-center gap-1.5 cursor-pointer shadow-xs"
                            >
                              <UserCheck className="w-3.5 h-3.5" />
                              <span>Átlépés a profilba</span>
                            </button>
                          </div>
                        ) : hasMissing ? (
                          <div className="divide-y divide-slate-200 dark:divide-slate-800 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-xs">
                            {item.uncovered_points?.map((u) => (
                              <div key={`u${u.pont}`} className="p-3.5 sm:px-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
                                <div className="space-y-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-mono font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/50 px-2 py-0.5 rounded border border-indigo-200 dark:border-indigo-800">{u.pont}</span>
                                    <span className="font-bold text-slate-900 dark:text-white">{u.cim}</span>
                                    <span className="text-slate-400">·</span>
                                    <span className="text-slate-500 font-medium">{u.chapter}</span>
                                  </div>
                                  <div className="text-[11px] text-rose-600 dark:text-rose-400 font-medium">⚠️ A 2026-os közzétételi lista pontjához nincs bejegyzés</div>
                                </div>
                              </div>
                            ))}
                            {item.missing_items.map((m) => (
                              <div 
                                key={m.id}
                                className="p-3.5 sm:px-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs hover:bg-slate-50/80 dark:hover:bg-slate-800/30 transition"
                              >
                                <div className="space-y-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-mono font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/50 px-2 py-0.5 rounded border border-indigo-200 dark:border-indigo-800">
                                      {m.pont || 'Pont nélkül'}
                                    </span>
                                    <span className="font-bold text-slate-900 dark:text-white">
                                      {m.megnevezes}
                                    </span>
                                    <span className="text-slate-400">·</span>
                                    <span className="text-slate-500 font-medium">
                                      Oldal: {m.page_title}
                                    </span>
                                  </div>
                                  <div className="text-[11px] text-rose-600 dark:text-rose-400 flex items-center gap-1 font-medium">
                                    <span>⚠️ {m.reason}</span>
                                    <span>(csatolt fájlok: {m.attachment_count} db)</span>
                                  </div>
                                </div>

                                <button
                                  onClick={() => handleFixMissingItem(item.tenant_id, item.tenant_name, m.page_id)}
                                  className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-xs font-semibold shadow-xs transition cursor-pointer whitespace-nowrap self-start sm:self-center"
                                  title="Átlépés a szervezethez és közvetlen ugrás a kérdéses oldalhoz a feltöltéshez"
                                >
                                  <Upload className="w-3.5 h-3.5" />
                                  <span>Pótlás feltöltéssel</span>
                                  <ArrowRight className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-xs text-slate-500 py-2">
                            A szervezet minden kötelező jogszabályi adatát és mellékletét hiánytalanul rögzítette.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════
          B) EGYÉNI INTÉZMÉNY RÉSZLETES NÉZET (SAJÁT OLDALAK STATISZTIKÁJA)
          ════════════════════════════════════════════════════════════════ */}
      {(!isSuperadmin || activeTab === 'single') && (
        <div className="space-y-8">
          {/* Top Centered Badge & Hero */}
          <div className="text-center space-y-3 pt-2">
            <div className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800/60 text-xs font-semibold shadow-xs">
              <Building className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
              <span>{tenant?.name || 'Közfeladatot ellátó szerv'}</span>
            </div>

            <div className="space-y-1">
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white">
                Közadat
              </h1>
              <h2 className="text-3xl sm:text-4xl font-bold font-serif text-emerald-800 dark:text-emerald-400">
                Nyilvántartó
              </h2>
            </div>

            <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 max-w-xl mx-auto">
              Közfeladatot ellátó szervek adatainak kezelése az információs törvény melléklete alapján.
            </p>
          </div>

          {/* 3 Metric Boxes */}
          <div className="grid grid-cols-3 gap-3 sm:gap-6 max-w-md sm:max-w-xl mx-auto">
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 sm:p-5 text-center shadow-xs">
              <div className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white font-sans">
                {totalPages}
              </div>
              <div className="text-[10px] sm:text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mt-1">
                Oldalak
              </div>
            </div>

            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 sm:p-5 text-center shadow-xs">
              <div className="text-3xl sm:text-4xl font-extrabold text-emerald-600 dark:text-emerald-400 font-sans">
                {single?.total_records ?? 0}
              </div>
              <div className="text-[10px] sm:text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mt-1">
                Rekordok
              </div>
            </div>

            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 sm:p-5 text-center shadow-xs flex flex-col justify-center">
              <div className="text-base sm:text-lg font-bold text-slate-800 dark:text-slate-200 truncate">
                {getRoleDisplay(role)}
              </div>
              <div className="text-[10px] sm:text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mt-1">
                Szerepkör
              </div>
            </div>
          </div>

          {/* Overall Statutory Progress Bar */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Jogszabályi kötelező adatok kitöltöttsége
              </span>
              <div className="flex-1 h-px bg-slate-200 dark:bg-slate-800"></div>
            </div>

            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-xs space-y-3">
              <div className="flex flex-wrap items-center justify-between text-xs sm:text-sm font-bold gap-2">
                <div className="flex items-center gap-2 text-slate-900 dark:text-slate-100">
                  {totalRecords === 0 ? (
                    <Clock className="w-4 h-4 text-slate-400" />
                  ) : singleMissing === 0 ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  ) : overallPercentage < 40 ? (
                    <span className="text-rose-600 dark:text-rose-400 font-extrabold text-base">!</span>
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                  )}
                  <span>
                    {totalRecords === 0
                      ? `Még nincsenek rögzített közzétételi pontok (${totalPages} nyilvántartási oldal)`
                      : `Összesített kitöltöttség (${totalPages} oldal, ${totalRecords} elvárt tétel${single && single.uncovered_points.length ? `, ebből ${single.uncovered_points.length} bejegyzés nélküli listapont` : ''})`}
                  </span>
                </div>
                <div className="text-slate-700 dark:text-slate-300 font-mono text-xs sm:text-sm font-bold">
                  {totalRecords === 0 ? '0 / 0 · Feltöltésre vár' : `${totalCompletedRecords} / ${totalRecords} · ${overallPercentage}%`}
                </div>
              </div>

              <div className="w-full bg-slate-100 dark:bg-slate-800 h-3 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    totalRecords === 0 ? 'bg-slate-300 dark:bg-slate-700' : getProgressBarColor(overallPercentage)
                  }`}
                  style={{ width: `${totalRecords === 0 ? 0 : Math.max(overallPercentage, 2)}%` }}
                />
              </div>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">
                Szigorú mérés: egy tétel csak akkor kitöltött, ha van valódi tartalma vagy melléklete, a DPO-szabály szerinti kötelező adat és melléklet megvan, és a kötelező mezők ki vannak töltve. Az import „Mappa: …” helyőrző szövege nem tartalom{single?.uses_template ? '; a 2026-os közzétételi lista bejegyzés nélküli pontjai is hiánynak számítanak' : ''}.
              </p>
              {single && singleMissing > 0 && (
                <div className="pt-2 border-t border-slate-100 dark:border-slate-800 space-y-2">
                  <div className="flex flex-wrap gap-1.5 text-[11px]">
                    {Object.entries(single.counts).filter(([, n]) => n > 0).map(([k, n]) => (
                      <span key={k} className="px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 font-semibold">
                        {n} · {CATEGORY_LABEL[k] || k}
                      </span>
                    ))}
                  </div>
                  <details open={showAllMissing} onToggle={(e) => setShowAllMissing((e.target as HTMLDetailsElement).open)} className="text-xs">
                    <summary className="cursor-pointer font-semibold text-slate-700 dark:text-slate-300">Hiánylista ({singleMissing} tétel)</summary>
                    <div className="mt-2 max-h-[28rem] overflow-auto divide-y divide-slate-100 dark:divide-slate-800 rounded-xl border border-slate-200 dark:border-slate-800">
                      {single.missing_items.map((m) => (
                        <div key={`r${m.id}`} className="p-2.5 flex flex-wrap items-center justify-between gap-2">
                          <div className="min-w-0">
                            <span className="font-mono font-bold text-indigo-600 dark:text-indigo-400 mr-1.5">{m.pont || '–'}</span>
                            <span className="font-semibold text-slate-900 dark:text-white">{m.megnevezes}</span>
                            <span className="text-slate-400"> · {m.page_title}</span>
                            <div className="text-[11px] text-rose-600 dark:text-rose-400">{m.reason}</div>
                          </div>
                          <button type="button" onClick={() => handleOpenPage(m.page_id)} className="px-2.5 py-1 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-semibold cursor-pointer">Megnyitás →</button>
                        </div>
                      ))}
                      {single.uncovered_points.map((u) => (
                        <div key={`u${u.pont}`} className="p-2.5 flex flex-wrap items-center justify-between gap-2">
                          <div className="min-w-0">
                            <span className="font-mono font-bold text-indigo-600 dark:text-indigo-400 mr-1.5">{u.pont}</span>
                            <span className="font-semibold text-slate-900 dark:text-white">{u.cim}</span>
                            <span className="text-slate-400"> · {u.chapter}</span>
                            <div className="text-[11px] text-rose-600 dark:text-rose-400">A 2026-os közzétételi lista pontjához nincs bejegyzés</div>
                          </div>
                          {u.page_id && <button type="button" onClick={() => handleOpenPage(u.page_id!)} className="px-2.5 py-1 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-semibold cursor-pointer">Megnyitás →</button>}
                        </div>
                      ))}
                    </div>
                  </details>
                </div>
              )}
            </div>
          </div>

          {/* Statutory Pages Cards Grid */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Nyilvántartási oldalak
              </span>
              <div className="flex-1 h-px bg-slate-200 dark:bg-slate-800"></div>
            </div>

            {loadingSingle ? (
              <div className="py-16 text-center text-slate-500 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto text-indigo-500 mb-2" />
                Oldalak és közzétételi adatok elemzése...
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {pageStats.map(({ page, totalRecords, completedRecords, percentage, missing }) => {
                  const isLow = totalRecords > 0 && percentage < 40;

                  return (
                    <div
                      key={page.id}
                      className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-xs flex flex-col justify-between hover:border-slate-300 dark:hover:border-slate-700 transition"
                    >
                      <div className="space-y-3">
                        {/* Icon & Title */}
                        <div>
                          <div className="text-2xl mb-1.5">{page.icon || '🏛️'}</div>
                          <h3 className="text-sm font-bold text-slate-900 dark:text-white line-clamp-2">
                            {page.title}
                          </h3>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">
                            {page.description || 'Közzétételi lista - jogszabályi pontok'}
                          </p>
                        </div>

                        {/* Completeness bar */}
                        <div className="space-y-1.5 pt-1">
                          <div className="flex items-center justify-between text-xs">
                            <span className="flex items-center gap-1 font-semibold text-slate-700 dark:text-slate-300">
                              {totalRecords === 0 ? (
                                <span className="text-slate-400">○</span>
                              ) : missing === 0 ? (
                                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                              ) : isLow ? (
                                <span className="text-rose-600 dark:text-rose-400 font-bold">!</span>
                              ) : (
                                <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                              )}
                              <span>Kitöltöttség</span>
                            </span>
                            <span className="font-mono text-xs font-bold text-slate-600 dark:text-slate-400">
                              {totalRecords === 0 ? '0/0 · Feltöltésre vár' : `${completedRecords}/${totalRecords} · ${percentage}%`}
                            </span>
                          </div>

                          <div className="w-full bg-slate-100 dark:bg-slate-800 h-1.5 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all duration-300 ${
                                totalRecords === 0 
                                  ? 'bg-slate-300 dark:bg-slate-700' 
                                  : isLow ? 'bg-rose-500' : missing > 0 ? 'bg-amber-500' : 'bg-emerald-500'
                              }`}
                              style={{ width: `${totalRecords === 0 ? 0 : Math.max(percentage, 3)}%` }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Bottom Row: Record count & Open button */}
                      <div className="flex items-center justify-between pt-4 mt-3 border-t border-slate-100 dark:border-slate-800 text-xs">
                        <span className="text-slate-500 dark:text-slate-400 font-medium">
                          {totalRecords} tétel{missing > 0 ? <span className="text-rose-600 dark:text-rose-400 font-semibold"> · {missing} hiány</span> : null}
                        </span>

                        <button
                          onClick={() => handleOpenPage(page.id)}
                          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 active:bg-blue-700 text-white font-medium rounded-lg text-xs transition shadow-xs cursor-pointer flex items-center gap-1"
                        >
                          <span>Megnyitás</span>
                          <span>→</span>
                        </button>
                      </div>
                    </div>
                  );
                })}

                {/* + Új oldal dashed card */}
                {['superadmin', 'tenant_admin', 'org_admin'].includes(role) && (
                  <button
                    onClick={() => setIsNewPageModalOpen(true)}
                    className="border-2 border-dashed border-slate-300 dark:border-slate-700 hover:border-indigo-400 dark:hover:border-indigo-500 rounded-2xl p-6 flex flex-col items-center justify-center gap-2.5 text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 bg-white/40 dark:bg-slate-900/30 transition min-h-[190px] cursor-pointer"
                  >
                    <div className="w-9 h-9 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                      <Plus className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                    </div>
                    <span className="text-xs font-bold">Új oldal</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── ÚJ OLDAL LÉTREHOZÁSA MODAL ──────────────────────────── */}
      {isNewPageModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in">
          <div className="w-full max-w-md rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-6 space-y-4 shadow-xl">
            <h3 className="font-bold text-base text-slate-900 dark:text-white">
              Új Közzétételi Oldal Létrehozása
            </h3>

            <form onSubmit={handleCreatePage} className="space-y-4 text-xs">
              <div>
                <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  Oldal Megnevezése *
                </label>
                <input
                  type="text"
                  required
                  value={newPageTitle}
                  onChange={(e) => setNewPageTitle(e.target.value)}
                  placeholder="pl. Környezetvédelmi adatok"
                  className="w-full p-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  Leírás / Törvényi Hivatkozás
                </label>
                <textarea
                  rows={2}
                  value={newPageDesc}
                  onChange={(e) => setNewPageDesc(e.target.value)}
                  placeholder="Infotv. melléklet pontjai vagy saját közzétételi szabályzat"
                  className="w-full p-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  Ikon (Emoji)
                </label>
                <input
                  type="text"
                  value={newPageIcon}
                  onChange={(e) => setNewPageIcon(e.target.value)}
                  className="w-20 p-2 text-center bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl text-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setIsNewPageModalOpen(false)}
                  className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl font-medium cursor-pointer"
                >
                  Mégse
                </button>
                <button
                  type="submit"
                  disabled={isCreatingPage}
                  className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-medium shadow-md transition disabled:opacity-50 cursor-pointer"
                >
                  {isCreatingPage ? 'Mentés...' : 'Oldal Létrehozása'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
